// @ts-nocheck
import React, { useId, useRef, useState } from "react";
import * as services from "../Services/services";
import { formatDue } from "../utils/lessonMedia";
import { learnerSummary, statusWord, validatePerson } from "../utils/lmsLearner";
import { ok, message, payload, useLoad, Status, DownloadButton } from "./learningShared";

/*
 * Team — the view for a customer's training admin (AVS OS /me says isTrainingAdmin). Everything here
 * is scoped by AVS OS to the admin's own account; this page never names an account. Three parts:
 * colleagues' progress (with a transcript per person), adding a colleague, and sign-up requests from
 * people at the same company who asked to join.
 */

function LearnerRow({ learner }) {
  const s = learnerSummary(learner);
  const detailsId = `learner-${learner.learnerId}`;
  const [open, setOpen] = useState(false);
  const who = learner.name || learner.email || "This person";
  return (
    <>
      <tr data-testid="learning-team-row">
        <td>
          <button
            type="button"
            className="btn btn-link p-0 text-start text-decoration-none"
            aria-expanded={open}
            aria-controls={detailsId}
            onClick={() => setOpen((o) => !o)}
            data-testid="learning-team-toggle"
          >
            <i className={`fa-solid ${open ? "fa-chevron-down" : "fa-chevron-right"} me-2 small`} aria-hidden="true" />
            {learner.name || learner.email}
          </button>
          {learner.name && learner.email && <div className="small text-body-secondary">{learner.email}</div>}
        </td>
        <td className="text-nowrap">
          {s.done} of {s.total}
          {s.overdue > 0 && <span className="badge text-bg-danger ms-2">{s.overdue} overdue</span>}
        </td>
        <td className="text-end">
          <DownloadButton
            kind="admin-transcript-pdf"
            id={learner.learnerId}
            fileLabel={learner.name || "learner"}
            label={<>Transcript<span className="visually-hidden"> for {who}</span></>}
            icon="fa-solid fa-file-pdf"
            testid="learning-team-transcript"
          />
        </td>
      </tr>
      {open && (
        <tr id={detailsId}>
          <td colSpan={3} className="bg-body-tertiary">
            {learner.courses.length === 0 ? (
              <span className="small text-body-secondary">No courses yet.</span>
            ) : (
              <table className="table table-sm mb-0 learning-table" aria-label={`Courses for ${who}`}>
                <thead>
                  <tr>
                    <th scope="col">Course</th>
                    <th scope="col">Status</th>
                    <th scope="col">Progress</th>
                    <th scope="col">Due</th>
                    <th scope="col">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {learner.courses.map((c) => (
                    <tr key={c.courseId}>
                      <td>{c.name}</td>
                      <td>
                        {statusWord(c.status)}
                        {c.overdue && c.status !== "completed" && <span className="badge text-bg-danger ms-2">Overdue</span>}
                      </td>
                      <td>{c.progressPct ?? 0}%</td>
                      <td className="text-nowrap">{formatDue(c.dueAt) || "—"}</td>
                      <td className="text-nowrap">{formatDue(c.completedAt) || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function AddPerson({ onAdded }) {
  const id = useId();
  const [form, setForm] = useState({ email: "", firstName: "", lastName: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const emailRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setDone(null);
    const problem = validatePerson(form);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    const resp = await services.lmsAdminAddPerson({
      email: form.email.trim(),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
    });
    setBusy(false);
    if (!ok(resp)) {
      setError(message(resp, "That person couldn't be added, please try again."));
      return;
    }
    const name = [form.firstName.trim(), form.lastName.trim()].filter(Boolean).join(" ");
    setDone(`${name} has been added. They'll see their training the next time they sign in to Copilot.`);
    setForm({ email: "", firstName: "", lastName: "" });
    emailRef.current?.focus();
    onAdded?.(payload(resp));
  };

  return (
    <form className="card learning-card mb-4" onSubmit={submit} noValidate aria-labelledby={`${id}-title`} data-testid="learning-add-person">
      <div className="card-body">
        <h2 id={`${id}-title`} className="h6 mb-1">
          Add a colleague
        </h2>
        <p className="small text-body-secondary mb-3">Use their work email. They must work at your company.</p>
        <div className="row g-2">
          <div className="col-12 col-md-5">
            <label className="form-label small" htmlFor={`${id}-email`}>
              Work email
            </label>
            <input
              ref={emailRef}
              id={`${id}-email`}
              type="email"
              autoComplete="off"
              className="form-control form-control-sm"
              value={form.email}
              onChange={set("email")}
              required
              aria-invalid={error === "Enter a valid work email address."}
              data-testid="learning-add-email"
            />
          </div>
          <div className="col-6 col-md-3">
            <label className="form-label small" htmlFor={`${id}-first`}>
              First name
            </label>
            <input id={`${id}-first`} className="form-control form-control-sm" value={form.firstName} onChange={set("firstName")} maxLength={80} data-testid="learning-add-first" />
          </div>
          <div className="col-6 col-md-3">
            <label className="form-label small" htmlFor={`${id}-last`}>
              Last name
            </label>
            <input id={`${id}-last`} className="form-control form-control-sm" value={form.lastName} onChange={set("lastName")} maxLength={80} required data-testid="learning-add-last" />
          </div>
          <div className="col-12 col-md-1 d-flex align-items-end">
            <button type="submit" className="btn btn-primary btn-sm w-100" disabled={busy} data-testid="learning-add-submit">
              {busy ? "Adding…" : "Add"}
            </button>
          </div>
        </div>
        {error && (
          <p className="small text-danger mt-2 mb-0" role="alert" data-testid="learning-add-error">
            {error}
          </p>
        )}
        {done && (
          <p className="small text-success-emphasis mt-2 mb-0" role="status" data-testid="learning-add-done">
            {done}
          </p>
        )}
      </div>
    </form>
  );
}

function SignupRequests({ onDecided }) {
  const { loading, error, data, reload } = useLoad(() => services.lmsAdminSignupRequests(), []);
  const [busy, setBusy] = useState(null);
  const [rowError, setRowError] = useState({});
  const [note, setNote] = useState(null);
  const pending = (data?.requests || []).filter((r) => !r.status || r.status === "pending");

  const decide = async (r, decision) => {
    setBusy(r.id);
    setRowError((e) => ({ ...e, [r.id]: null }));
    const resp = await services.lmsAdminDecideSignup(r.id, decision);
    setBusy(null);
    if (!ok(resp)) {
      setRowError((e) => ({ ...e, [r.id]: message(resp, "That didn't save, please try again.") }));
      return;
    }
    const who = [r.firstName, r.lastName].filter(Boolean).join(" ") || r.email;
    setNote(decision === "approve" ? `${who} can now take training.` : `${who}'s request was turned down.`);
    reload();
    if (decision === "approve") onDecided?.();
  };

  return (
    <section className="mb-4" aria-labelledby="learning-signups" data-testid="learning-signups">
      <h2 id="learning-signups" className="h6 text-body-secondary text-uppercase mb-2">
        Requests to join
      </h2>
      {note && (
        <p className="small text-success-emphasis" role="status" data-testid="learning-signup-note">
          {note}
        </p>
      )}
      <Status loading={loading} error={error} onRetry={reload} />
      {data && pending.length === 0 && <p className="small text-body-secondary mb-0">No one is waiting.</p>}
      {pending.length > 0 && (
        <ul className="list-unstyled mb-0">
          {pending.map((r) => {
            const who = [r.firstName, r.lastName].filter(Boolean).join(" ") || r.email;
            return (
              <li key={r.id} className="d-flex flex-wrap align-items-center gap-2 py-2 border-top" data-testid="learning-signup-row">
                <div className="flex-grow-1">
                  <div>{who}</div>
                  <div className="small text-body-secondary">
                    {r.email}
                    {r.createdAt && <> · asked {formatDue(r.createdAt)}</>}
                  </div>
                  {rowError[r.id] && (
                    <div className="small text-danger" role="alert">
                      {rowError[r.id]}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  disabled={busy === r.id}
                  onClick={() => decide(r, "approve")}
                  aria-label={`Approve ${who}`}
                  data-testid="learning-signup-approve"
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={busy === r.id}
                  onClick={() => decide(r, "reject")}
                  aria-label={`Turn down ${who}`}
                  data-testid="learning-signup-reject"
                >
                  Turn down
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default function LearningAdmin() {
  const { loading, error, data, reload } = useLoad(() => services.lmsAdminProgress(), []);
  return (
    <div data-testid="learning-admin">
      <p className="text-body-secondary mb-3">
        {data?.account?.name ? `Training for everyone at ${data.account.name}.` : "Training for everyone at your company."}
      </p>
      <AddPerson onAdded={reload} />
      <SignupRequests onDecided={reload} />
      <section aria-labelledby="learning-team">
        <h2 id="learning-team" className="h6 text-body-secondary text-uppercase mb-2">
          Progress
        </h2>
        <Status loading={loading} error={error} onRetry={reload} />
        {data && data.learners.length === 0 && <p className="small text-body-secondary">No one at your company has training yet.</p>}
        {data && data.learners.length > 0 && (
          <div className="table-responsive">
            <table className="table align-middle learning-table" data-testid="learning-team-table">
              <thead>
                <tr>
                  <th scope="col">Person</th>
                  <th scope="col">Courses complete</th>
                  <th scope="col">
                    <span className="visually-hidden">Transcript</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.learners.map((l) => (
                  <LearnerRow key={l.learnerId} learner={l} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
